import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import useConversations from '@/features/chat/hooks/use-conversations.hook'
import useGroupConversationsWithoutFirstMessage from '@/features/chat/hooks/use-group-conversations-without-first-message.hook'
import { getGroupsNotifications, V1_MENTORSHIP_GROUPS_NOTIFICATIONS_QUERY_KEY } from '@/features/groups/api'
import useSessions from '@/features/sessions/hooks/use-sessions.hook'
import { getUncompletedSessions } from '@/features/sessions/utils'

function useNotifications() {
  const { accountRef } = useAccount()
  const { sessionsRef } = useSessions()
  const { conversationsRef } = useConversations()

  const { isAuthRef } = useIsAuth()

  const { data } = useQuery({
    queryKey: [V1_MENTORSHIP_GROUPS_NOTIFICATIONS_QUERY_KEY],
    queryFn: () => getGroupsNotifications(),
    enabled: !!isAuthRef,
  })

  // group conversations without first message sent
  const { groupConversationsWithoutFirstMessageSentRef } = useGroupConversationsWithoutFirstMessage()

  // group conversations without seen messages
  const groupConversationsNotSeenRef = computed(() =>
    conversationsRef.value.filter((conversation) => {
      return !conversation.users.find((user) => user.id === accountRef.value?.id)?.hasSeen
    }),
  )

  // sessions to complete
  const toCompleteSessionsRef = computed(() => sessionsRef.value.filter(getUncompletedSessions))

  // all messages notifications
  const messagesNotificationsRef = computed(
    () => groupConversationsNotSeenRef.value.length + groupConversationsWithoutFirstMessageSentRef.value.length,
  )

  // all sessions notifications
  const sessionsNotificationsRef = computed(() => toCompleteSessionsRef.value.length)

  // all pending actions
  const pendingActionsRef = computed(() => messagesNotificationsRef.value + sessionsNotificationsRef.value)

  return {
    allNotificationsPerGroupRef: data,
    groupConversationsWithoutFirstMessageSentRef,
    groupConversationsNotSeenRef,
    messagesNotificationsRef,
    sessionsNotificationsRef,
    pendingActionsRef,
  }
}

export default useNotifications
